import { NumberFormatter } from '@ddt-web/data-service';
import { StringUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { StackedDataUtil } from '../../data-mapper/utils';
import { IHighChartsAttributesModel } from '../../models/high-charts-attributes.interface';
import { BaseChartWorker } from '../common';

/**
 * StackedGroupedColumnWorker: chart worker specific to the scenario analysis column chart.
 * @param {any} config JSON object representing the chart configuration.
 * @param {IHighChartsAttributesModel} highChartsAttributes an object containing properties used to augment the chart configuration.
 */
export class StackedGroupedColumnWorker extends BaseChartWorker {
	constructor(config: any, highChartsAttributes: IHighChartsAttributesModel) {
		super(config, highChartsAttributes);
		if (this.highChartsAttributes.isPdf) {
			this.initChartForPdf();
		} else {
			this.initChartForWeb();
		}
	}

	private initChartForWeb(): void {
		this.initTooltip();
	}

	private initChartForPdf(): void { }

	private initTooltip(): void {
		const colors = this.config.colors;
		this.config.tooltip.formatter = function (): string {
			const stackName = get(this.point, 'series.userOptions.stack') || get(this.point, 'series.stackKey', '').replace('column', '');
			const portfolioIndex = this.series.chart.options.extensibleData.portfolios.findIndex(x => x === stackName);
			const numberFormatter = new NumberFormatter('%');
			let tooltipHtml = '';
			let sum = 0;
			const seriesLength = this.series.chart.options.extensibleData.series.length;
			this.series.chart.options.extensibleData.series.forEach((category, index) => {
				const defaultStyleOption = StackedDataUtil.getLegendMarkerAttributes(
					colors[index],
					this.series.chart.options.extensibleData.portfolios,
					portfolioIndex,
					category.name
				);
				if (category.stack === stackName) {
					sum += Number(category.data[this.point.index]);
					tooltipHtml += `
						<div class="wrapper" style="order:${seriesLength - index}">
							<div ${defaultStyleOption}>&#9679</div>
							<div class="grid-item">
								<span>${StringUtil.escapeHtml(category.name)}</span>
							</div>
							<div class="grid-item category-values">
								${numberFormatter.format(category.data[this.point.index])}
							</div>
						</div>
						`;
				}
			});
			const render = `
			<div class="tooltip-container">
				<div class="inner-content">
					<div class="tooltip-title wrapper">
						<div class="header-item">
							<span>${StringUtil.escapeHtml(stackName)}</span>
						</div>
						<div class="grid-item category-values">
							<span>${numberFormatter.format(sum)}</span>
						</div>
					</div>
					${tooltipHtml}
				</div>
			</div>
			`;
			return render;
		};
	}

	/**
	 * transformData: transform the data into consumable format for the scenario analysis column chart.
	 */
	public transformData(): void {
		const transformedData = StackedDataUtil.getStackedData(this.highChartsAttributes.tabData, this.highChartsAttributes.portfolioIndex);
		const series = transformedData.series;
		series.reverse();
		const portfolios = transformedData.portfolios;
		this.config.extensibleData = { series, portfolios };
		this.config.series = series;
		this.config.xAxis.categories = transformedData.scenarios;
	}
}
